import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import ButtonGroup from "../../components/ButtonGroup";
import CustomTableSimple from "../../components/CustomTableSimple";
import ErrorMessage from "../../components/ErrorMessage";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import Main from "../../components/Main";
import SideMenu from "../../components/SideMenu";
import SimpleButton from "../../components/SimpleButton";
import Spacer from "../../components/Spacer";
import SubPage from "../../components/SubPage";
import SubPagesContainer from "../../components/SubPagesContainer";
import history from "../../providers/history";
import { IApplicationState } from "../../store";
import { profileUserRequest } from "../../store/authentication/slice";
import { systemAddDialog } from "../../store/system/slice";
import { startEditingUsers, usersListRequest, usersRemoveRequest } from "../../store/users/slice";
import UserChangePasswordForm from "../UserChangePasswordForm";
import UserForm from "../UserForm";
import { Container, Content } from "./styles";

const Users: React.FC = () => {
    const users = useSelector((state: IApplicationState) => state.users)

    const { t } = useTranslation();

    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(usersListRequest(undefined))
        dispatch(profileUserRequest())
    }, [dispatch]);

    return <Container id="container">
        <Header />

        <Content>
            <SideMenu />
            <Main>
                <h1>{t("pages.users.title")}</h1>

                <Spacer size={20} />
                {users.isLoading && <React.Fragment>
                    <p>{t("pages.users.loading")}</p>
                </React.Fragment>}

                <ButtonGroup>
                    <SimpleButton color="primary" prependIcon="plus" onClick={() => {
                        dispatch(startEditingUsers({
                            name: "",
                            email: "",
                            password: "",
                            status: true,
                        }))
                        history.push("users/form")
                    }}>{t("pages.users.buttons.add_user")}</SimpleButton>
                </ButtonGroup>

                <Spacer size={20} />
                {!users.isLoading && !users.error && users.data.length === 0 && (
                    <React.Fragment>
                        <ErrorMessage>
                            {t("pages.users.not_found")}
                        </ErrorMessage>
                    </React.Fragment>
                )}

                <Spacer size={20} />

                {!users.isLoading && !users.error && users.data.length > 0 && <React.Fragment>
                    <CustomTableSimple>
                        <thead>
                            <tr>
                                <th style={{ minWidth: 250 }}>{t("pages.users.name")}</th>
                                <th style={{ minWidth: 80 }}>{t("pages.users.status")}</th>
                                <th style={{ minWidth: 150, width: 150 }}>{t("pages.users.actions")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.data.map(user => <tr key={`user-id-${user.id}`}>
                                {user.isDeleting && <React.Fragment>
                                    <td colSpan={3}>{t("pages.users.deleting")}</td>
                                </React.Fragment>}

                                {!user.isDeleting && <React.Fragment>
                                    <td>
                                        {user.name}
                                        {user.error && <p>{user.error.message}</p>}
                                    </td>
                                    <td>
                                        {user.status ? t("pages.users.active") : t("pages.users.inactive")}
                                    </td>
                                    <td>
                                        <ButtonGroup>
                                            <SimpleButton size={'small'} prependIcon='pencil' color='primary' onClick={() => {
                                                dispatch(startEditingUsers(user))
                                                history.push(`/users/${user.id}/form`)
                                            }}>{t("common.buttons.edit")}</SimpleButton>
                                            <SimpleButton size={'small'} prependIcon='trash' color='danger' onClick={() => {
                                                return dispatch(systemAddDialog({
                                                    title: t("pages.users.dialogs.remove_user"),
                                                    message: t("pages.users.dialogs.remove_user_message"),
                                                    actions: [{
                                                        text: t("pages.users.buttons.remove_user_confirm"),
                                                        action: () => {
                                                            dispatch(usersRemoveRequest(user.id || ''));
                                                            history.push(`/users/`)
                                                        },
                                                        color: 'danger',
                                                        prependIcon: 'trash'
                                                    },
                                                    {
                                                        text: t("pages.users.buttons.remove_user_cancel"),
                                                        action: () => { },
                                                        color: 'none'
                                                    }]
                                                }));
                                            }}>{t("common.buttons.delete")}</SimpleButton>
                                        </ButtonGroup>
                                    </td>
                                </React.Fragment>}
                            </tr>)}
                        </tbody>
                    </CustomTableSimple>
                    <Spacer size={20} />

                </React.Fragment>}
            </Main>
        </Content>
        <Routes>
            <Route path={'/:idUser/form'} element={(
                <SubPagesContainer>
                    <SubPage>
                        <UserForm />
                    </SubPage>
                </SubPagesContainer>
            )} />
            <Route path={'/form'} element={(
                <SubPagesContainer>
                    <SubPage>
                        <UserForm />
                    </SubPage>
                </SubPagesContainer>
            )} />
            <Route path={'/users/change-password/form'} element={(
                <SubPagesContainer>
                    <SubPage>
                        <UserChangePasswordForm />
                    </SubPage>
                </SubPagesContainer>
            )} />
        </Routes>
        <Footer />
    </Container>
}

export default Users;